// src/hooks/useHealthCareWorkerVisitStatus.ts
import { infantSelectors } from '@/store/infant';
import { motherSelectors } from '@/store/mother';
import { VisitDto, VisitStatusDto } from '@ecdlink/core';
import { startOfWeek, startOfDay, isWithinInterval } from 'date-fns';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

export const useHealthCareWorkerVisitStatus = (): VisitStatusDto => {
  const motherVisits = useSelector(
    motherSelectors.getDashboardMotherVisitsSelector
  );
  const infantVisits = useSelector(
    infantSelectors.getDashboardInfantVisitsSelector
  );

  return useMemo(() => {
    const today = startOfDay(new Date());
    const monday = startOfWeek(today, { weekStartsOn: 1 }); // Monday = 1
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    // Keep only visits that match the exact backend filter logic
    const relevantVisits = [...motherVisits, ...infantVisits].filter(
      (visit) => {
        const plannedDate = startOfDay(new Date(visit.plannedVisitDate));

        const isUnattendedAndInOrBeforeThisWeek =
          !visit.attended && plannedDate <= sunday;
        const isAttendedAndWithinLastYear =
          visit.attended &&
          visit.actualVisitDate &&
          new Date(visit.actualVisitDate) >= new Date(currentYear - 1, 0, 1);

        return isUnattendedAndInOrBeforeThisWeek || isAttendedAndWithinLastYear;
      }
    );

    // Completed visits (type-narrowed so actualVisitDate is guaranteed)
    const completedMotherVisits = motherVisits.filter(
      (v): v is VisitDto & { actualVisitDate: string } =>
        v.attended && !!v.actualVisitDate
    );

    const completedChildVisits = infantVisits.filter(
      (v): v is VisitDto & { actualVisitDate: string } =>
        v.attended && !!v.actualVisitDate
    );

    // Overdue mothers → distinct clientId where at least one visit is past due
    const overdueMotherIds = new Set<string>();
    for (const visit of motherVisits) {
      if (visit.attended || !visit.clientId) continue;

      const due = visit.dueDate
        ? startOfDay(new Date(visit.dueDate))
        : startOfDay(new Date(visit.plannedVisitDate));

      if (due <= today) {
        overdueMotherIds.add(visit.clientId);
      }
    }

    // Due this week (unattended + planned date inside current week)
    const isThisWeek = (date: string | Date) =>
      isWithinInterval(new Date(date), { start: monday, end: sunday });

    const motherDueThisWeek = motherVisits.filter(
      (v) => !v.attended && isThisWeek(v.plannedVisitDate)
    ).length;

    const childDueThisWeek = infantVisits.filter(
      (v) => !v.attended && isThisWeek(v.plannedVisitDate)
    ).length;

    // Latest completed visit (mother or child)
    let lastCompletedVisit: Date | null = null;
    for (const visit of relevantVisits) {
      if (visit.attended && visit.actualVisitDate) {
        const date = new Date(visit.actualVisitDate);
        if (!lastCompletedVisit || date > lastCompletedVisit) {
          lastCompletedVisit = date;
        }
      }
    }

    return {
      motherVisitsCompletedThisMonth: completedMotherVisits.filter(
        (v) =>
          new Date(v.actualVisitDate).getMonth() === currentMonth &&
          new Date(v.actualVisitDate).getFullYear() === currentYear
      ).length,

      motherVisitsCompletedThisYear: completedMotherVisits.filter(
        (v) => new Date(v.actualVisitDate).getFullYear() === currentYear
      ).length,

      childVisitsCompletedThisMonth: completedChildVisits.filter(
        (v) =>
          new Date(v.actualVisitDate).getMonth() === currentMonth &&
          new Date(v.actualVisitDate).getFullYear() === currentYear
      ).length,

      childVisitsCompletedThisYear: completedChildVisits.filter(
        (v) => new Date(v.actualVisitDate).getFullYear() === currentYear
      ).length,

      motherOverDueVisits: overdueMotherIds.size,
      motherDueVisitsThisWeek: motherDueThisWeek,
      childDueVisitsThisWeek: childDueThisWeek,
      lastCompletedVisit: lastCompletedVisit ?? undefined,
    } as unknown as VisitStatusDto;
  }, [motherVisits, infantVisits]);
};
